import React, { useCallback, useContext } from 'react';
import { Button, Input, Row } from 'antd';
import { publicRuntimeConfig } from 'config/index';
import { Formik } from 'formik';
import { get } from 'lodash-es';
import { useRouter } from 'views/useRouter';

import { addErrorMessage, FormField, FormikFieldTouched } from 'components/common';
import { classnames } from 'lib/classnames';
import { AUTOMATION_MAX_NAME_LENGTH, UPDATE_BASIC_INFO } from 'lib/constants/automation';
import { useConstantConfig } from 'lib/hooks';
import useI18n from 'lib/hooks/useI18n';
import { SwitchMainProps } from 'lib/types/automation';
import { FormError } from 'lib/types/error';

import NodeContainer from './common/NodeContainer';
import { AddAutomationDropdown } from './AddAutomationDropdown';
import { AUTOMATION_NODE_PREFIX } from './constants';
import { Context } from './context';
import { useSaveAutomation } from './hooks';
import SwitchElement from './SwitchElement';
import { getNodeIndex } from './utils';

import styles from './SwitchComponent.module.less';

const cx = classnames(styles);
/**
 * @param props
 * @constructor
 * @description 进入当前组件的情况都是没有nodes的情况，需要自行判断将节点加到哪
 */
const SwitchMainType: React.FC<SwitchMainProps> = props => {
  const { nodes, onAddStorageNode, enableAuto, isTemplate } = props;
  const router = useRouter();
  const {
    query: { automation, scenes },
  } = router;
  const { t } = useI18n();
  const { INIT_NODE, SWITCH_COMPONENT_COMMON_LIST } = useConstantConfig();
  const { isDemo } = publicRuntimeConfig;

  const {
    state: { basicInfo },
    dispatch,
  } = useContext(Context);

  const changeDetails = useCallback(
    component => {
      const { type, title } = component;
      const componentData = { type, name: title };
      const path = get(nodes[nodes.length - 1], 'path', [0]);
      // 这块的path路径都是错误的会在后边add的时候调用updateNodesPath修正
      const newNode = Object.assign({}, INIT_NODE, {
        name: t('global.newNode'),
        path,
        key: `${AUTOMATION_NODE_PREFIX}${getNodeIndex(nodes)}`,
      });
      onAddStorageNode({ ...newNode, ...componentData }, newNode, true);
    },
    [INIT_NODE, nodes, onAddStorageNode, t],
  );

  const { saveAutomation } = useSaveAutomation({ nodes, enableAuto });

  const handleSave = useCallback(basicInfo => saveAutomation(basicInfo, scenes as string), [saveAutomation, scenes]);

  const handleSubmit = useCallback(
    value => {
      dispatch({
        type: UPDATE_BASIC_INFO,
        basicInfo: { ...basicInfo, ...value },
      });
      handleSave({ ...basicInfo, ...value });
    },
    [basicInfo, dispatch, handleSave],
  );

  const validate = useCallback(
    value => {
      const errors = {} as FormError;
      if (!value.name) {
        errors.name = addErrorMessage(errors.name, t('message.automation.noEmptyName'));
      }
      if (value.name?.length > AUTOMATION_MAX_NAME_LENGTH) {
        errors.name = addErrorMessage(
          errors.name,
          t('pages.automation.BasicInfo.default.message.nameMaxLength', { max: AUTOMATION_MAX_NAME_LENGTH }),
        );
      }
      return errors;
    },
    [t],
  );

  return (
    <div className={cx('switch-main')}>
      <div className={cx('component-main')}>
        <NodeContainer
          title={t('pages.automation.default.view.switch.NodeContainer.title')}
          desc={t('pages.automation.default.view.switch.NodeContainer.desc')}
        >
          <div className={cx('component-list')}>
            {SWITCH_COMPONENT_COMMON_LIST.map((component, index) => {
              return <SwitchElement onClickNode={changeDetails} element={component} key={index} />;
            })}
          </div>
        </NodeContainer>
      </div>

      <>
        <div className={cx('component-division')}>
          <span className={cx('component-division-word')}>{t('global.or')}</span>
        </div>
        <div className={cx('component-center')}>
          <Formik initialValues={basicInfo} onSubmit={handleSubmit} validate={validate}>
            {({ values, handleSubmit }) => {
              return (
                <div className={cx('component-center-form')}>
                  <FormikFieldTouched />
                  <FormField name="name">
                    {({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('placeholder.automation.name')}
                        maxLength={AUTOMATION_MAX_NAME_LENGTH}
                      />
                    )}
                  </FormField>
                  <Row justify="start">
                    {isTemplate ? (
                      <AddAutomationDropdown
                        nodes={nodes}
                        basicInfo={{ ...basicInfo, name: values.name }}
                        disabled={isDemo}
                      />
                    ) : (
                      <Button type="primary" onClick={() => handleSubmit()} disabled={isDemo}>
                        {automation === 'create' ? t('global.release') : t('global.saveAndUpdate')}
                      </Button>
                    )}
                  </Row>
                </div>
              );
            }}
          </Formik>
        </div>
      </>
    </div>
  );
};

export default SwitchMainType;
